import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  Calendar,
  Receipt,
  Euro,
  CreditCard,
  Wallet,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Download,
  FileText,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const ClientSchedulesPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [paying, setPaying] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletHourlyRate, setWalletHourlyRate] = useState(75);

  useEffect(() => {
    fetchSchedules();
    fetchWalletBalance();
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const res = await api.get('/wallet/');
      // Use available_hours (balance - reservations) instead of balance_amount
      const availableHours = res.data?.available_hours ?? res.data?.balance_hours ?? 0;
      const hourlyRate = res.data?.hourly_rate || 75;
      setWalletBalance(availableHours * hourlyRate);
      setWalletHourlyRate(hourlyRate);
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const res = await api.get('/client/payment-schedules');
      setSchedules(res.data || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInstallment = (schedule, installment) => {
    setSelectedInstallment({ schedule, installment });
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    if (!selectedInstallment) return;
    
    setPaying(true);
    try {
      await api.post(`/client/payment-schedules/${selectedInstallment.schedule.id}/pay`, {
        installment_number: selectedInstallment.installment.number,
        payment_method: paymentMethod
      });
      toast.success('Paiement effectué avec succès !');
      fetchSchedules();
      setShowPaymentModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setPaying(false);
    }
  };

  const downloadPdf = async (scheduleId) => {
    try {
      // Get the token explicitly for blob requests
      const token = localStorage.getItem('token');
      const response = await api.get(`/settings/payment-schedules/${scheduleId}/pdf`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `echeancier-${scheduleId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (installment) => {
    if (installment.paid) {
      return <Badge className="bg-emerald-500 text-white">Payé</Badge>;
    }
    const dueDate = new Date(installment.due_date);
    const today = new Date();
    if (dueDate < today) {
      return <Badge variant="destructive">En retard</Badge>;
    }
    const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    if (daysUntil <= 7) {
      return <Badge className="bg-amber-500 text-white">À venir</Badge>;
    }
    return <Badge variant="secondary">À payer</Badge>;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="client-schedules-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
            Mes échéanciers
          </h1>
          <p className="text-slate-500 mt-1">
            Consultez et payez vos échéances en cours
          </p>
        </div>

        {schedules.length === 0 ? (
          <Card className="card">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="font-semibold text-slate-700">Aucun échéancier</h3>
              <p className="text-sm text-slate-500 mt-1">
                Vous n'avez pas d'échéancier de paiement en cours
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {schedules.map(schedule => {
              const paidCount = schedule.installments?.filter(i => i.paid).length || 0;
              const totalCount = schedule.installments?.length || 0;
              const progress = totalCount > 0 ? (paidCount / totalCount) * 100 : 0;
              const paidAmount = schedule.installments?.filter(i => i.paid).reduce((sum, i) => sum + i.amount, 0) || 0;
              
              return (
                <Card key={schedule.id} className="card overflow-hidden">
                  <CardHeader className="bg-slate-50 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                          {schedule.title || schedule.project_name}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {schedule.project_name && schedule.project_name !== schedule.title && (
                            <span>Projet : {schedule.project_name} • </span>
                          )}
                          {totalCount} échéances • Créé le {new Date(schedule.created_at).toLocaleDateString('fr-FR')}
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: theme.colors?.accent }}>
                          {schedule.total_amount?.toLocaleString('fr-FR')}€ HT
                        </p>
                        <p className="text-sm text-slate-500">
                          {paidAmount.toLocaleString('fr-FR')}€ HT payé
                        </p>
                      </div>
                    </div>
                    
                    {/* Progress */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-slate-500">{paidCount} / {totalCount} échéances payées</span>
                        <span className="font-medium">{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                  </CardHeader>
                  
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {schedule.installments?.map((installment, idx) => (
                        <div 
                          key={idx}
                          className={cn(
                            "p-3 sm:p-4 hover:bg-slate-50 transition-colors",
                            installment.paid && "bg-emerald-50/50"
                          )}
                        >
                          {/* Mobile: Stack vertically */}
                          <div className="flex items-start gap-3">
                            <div className={cn(
                              "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold shrink-0 text-sm",
                              installment.paid 
                                ? "bg-emerald-100 text-emerald-600" 
                                : "bg-slate-100 text-slate-600"
                            )}>
                              {installment.paid ? <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" /> : idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-medium text-sm sm:text-base truncate">
                                    {installment.label || `Échéance ${idx + 1}`}
                                  </p>
                                  <p className="text-xs sm:text-sm text-slate-500 flex items-center gap-1">
                                    <Clock className="w-3 h-3 shrink-0" />
                                    {new Date(installment.due_date).toLocaleDateString('fr-FR', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </p>
                                </div>
                                <p className="font-bold text-sm sm:text-lg text-right whitespace-nowrap">
                                  {installment.amount?.toLocaleString('fr-FR')}€ HT
                                </p>
                              </div>
                              {/* Status and action on second row for mobile */}
                              <div className="flex items-center justify-between gap-2 mt-2">
                                <div className="flex items-center gap-2">
                                  {getStatusBadge(installment)}
                                  {installment.paid && installment.payment_date && (
                                    <span className="text-xs text-slate-400 hidden sm:inline">
                                      Payé le {new Date(installment.payment_date).toLocaleDateString('fr-FR')}
                                    </span>
                                  )}
                                </div>
                                {!installment.paid && (
                                  <Button 
                                    size="sm"
                                    className="h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3"
                                    style={{ backgroundColor: theme.colors?.accent }}
                                    onClick={() => handlePayInstallment(schedule, { ...installment, number: idx + 1 })}
                                  >
                                    Payer
                                    <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-0.5 sm:ml-1" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  
                  {/* Footer Actions */}
                  <div className="p-4 bg-slate-50 border-t flex items-center justify-between">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => downloadPdf(schedule.id)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger PDF
                    </Button>
                    
                    {schedule.project_id && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => window.location.href = `/projects/${schedule.project_id}`}
                      >
                        Voir le projet
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payer l'échéance</DialogTitle>
            <DialogDescription>
              Choisissez votre mode de paiement
            </DialogDescription>
          </DialogHeader>
          
          {selectedInstallment && (
            <div className="py-4 space-y-4">
              <div className="p-4 rounded-lg bg-slate-50 border">
                <p className="text-sm text-slate-500">Montant à payer</p>
                <p className="text-3xl font-bold" style={{ color: theme.colors?.accent }}>
                  {selectedInstallment.installment.amount?.toLocaleString('fr-FR')}€ HT
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  soit {(selectedInstallment.installment.amount * 1.2)?.toLocaleString('fr-FR')}€ TTC (TVA 20%)
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedInstallment.installment.label || `Échéance ${selectedInstallment.installment.number}`}
                </p>
              </div>
              
              <div className="space-y-2">
                <p className="font-medium">Mode de paiement</p>
                
                {(() => {
                  const walletBalanceEuros = walletBalance; // Already in euros, not cents
                  const installmentAmount = selectedInstallment?.installment?.amount || 0;
                  const hasEnoughBalance = walletBalanceEuros >= installmentAmount;
                  
                  return (
                    <button
                      onClick={() => setPaymentMethod('wallet')}
                      className={cn(
                        "w-full p-3 sm:p-4 rounded-lg border-2 flex items-center gap-3 transition-all",
                        paymentMethod === 'wallet' 
                          ? "border-emerald-500 bg-emerald-50" 
                          : "border-slate-200 hover:border-slate-300"
                      )}
                    >
                      <div className={cn(
                        "w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0",
                        paymentMethod === 'wallet' ? "bg-emerald-500 text-white" : "bg-slate-100"
                      )}>
                        <Wallet className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="text-left flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-sm sm:text-base">Wallet</p>
                          <p className={cn(
                            "text-xs sm:text-sm font-semibold",
                            hasEnoughBalance ? "text-emerald-600" : "text-red-500"
                          )}>
                            {walletBalanceEuros.toLocaleString('fr-FR')}€ HT
                          </p>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-500">
                          {hasEnoughBalance 
                            ? "Solde suffisant" 
                            : `Solde insuffisant (manque ${(installmentAmount - walletBalanceEuros).toLocaleString('fr-FR')}€)`
                          }
                        </p>
                      </div>
                    </button>
                  );
                })()}
                
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  className={cn(
                    "w-full p-3 sm:p-4 rounded-lg border-2 flex items-center gap-3 transition-all",
                    paymentMethod === 'stripe' 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <div className={cn(
                    "w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0",
                    paymentMethod === 'stripe' ? "bg-blue-500 text-white" : "bg-slate-100"
                  )}>
                    <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-medium text-sm sm:text-base">Carte bancaire</p>
                    <p className="text-xs sm:text-sm text-slate-500">Paiement sécurisé par Stripe</p>
                  </div>
                </button>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Annuler
            </Button>
            <Button 
              style={{ backgroundColor: theme.colors?.accent }}
              onClick={processPayment}
              disabled={paying}
            >
              {paying ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Confirmer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default ClientSchedulesPage;
